import React, { useState } from 'react';
import { withRouter } from 'react-router';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import moment from 'moment';

import BusinessApi from 'apis/Business';
import BillingAddressApi from 'apis/BillingAddress';
import Toaster from 'components/Toaster';
import { getRole } from 'constants/localStorageManager';
import { USERS } from 'constants/Users';
import { formatExpiryDate } from 'utils/date';
import visaIcon from 'assets/img/visa.svg';
import BusinessCardWrapper from '../businessCardWrapper';
import PlanSelectionModal from '../modal/planSelectionModal';
import ManageSubscription from '../../subscription/manageSubscription';
import AddCreditCardModal from '../../Modal/creditCard/addCreditCard';
import ChargeCreditCardModal from '../../Modal/creditCard/chargeCreditCard';
import AddCardSuccessModal from '../../Modal/creditCard/addCardSuccess';
import DeleteCardModal from '../../Modal/creditCard/deleteCreditCard';
import CustomIconToolTip from '../../tooltip/customToolTip';

const DEFAULT_TRIAL_LENGTH = 7;

const CARD_TYPES = {
  VISA: 'VISA',
  MASTERCARD: 'MASTERCARD',
};

const getCardImage = type => {
  const cardType = type ? type.toUpperCase() : type;
  let cardImg = null;

  switch (cardType) {
    case CARD_TYPES.VISA:
      cardImg = visaIcon;
      break;
  }

  return cardImg;
};

const getPlanSubscription = (planSubscription, trialLength) => {
  const startDate = new Date();
  const isTrial = planSubscription.value.id == 1;
  const endingInterval = isTrial ? trialLength : 1;
  const endingFrequency = isTrial ? 'days' : 'years';
  const endDate = moment(startDate).add(endingInterval, endingFrequency);

  const planSubscriptionAttributes = {
    start_date: startDate,
    end_date: endDate,
    plan_id: planSubscription.value.id,
    status: 'active',
  };

  return planSubscriptionAttributes;
};

const CreditCardItem = ({
  billingAddress,
  fetchBusinessDetail,
  toggleDeleteModal,
  toggleChargeCardModal,
  isSuperAdmin,
}) => {
  const {
    id,
    billing_name,
    is_primary,
    expiry_date,
    card_number,
    card_type,
  } = billingAddress;

  const setAsPrimary = () => {
    BillingAddressApi.setPrimary(id)
      .then(({ data }) => {
        Toaster.getSuccessToaster(
          'check-circle',
          'Successfully update payment card'
        );
        fetchBusinessDetail();
      })
      .catch(() => {
        Toaster.getErrorToaster(
          'exclamation-triangle',
          'Failed to update card'
        );
      });
  };

  const changeToPrimary = () => {
    if (is_primary) return;
    setAsPrimary();
  };

  const deleteCard = () => {
    if (is_primary)
      return Toaster.getErrorToaster(
        'exclamation-triangle',
        'Cannot delete primary card'
      );
    toggleDeleteModal({ id });
  };

  let cardImg = getCardImage(card_type);

  return (
    <div className="col-md-6">
      <div className="location-card mb-3">
        <div className="card-status">
          <FontAwesomeIcon
            icon="check-circle"
            className={is_primary ? 'active-status' : 'disabled-status'}
            onClick={changeToPrimary}
          />
          <FontAwesomeIcon
            icon="times-circle"
            className="disabled-status"
            onClick={deleteCard}
          />
        </div>
        <div className="card-address mb-4">
          <p>{billing_name}</p>
          <p>{card_number}</p>
          <p>
            <span className="thin-font-weight">Expiration Date: </span>
            {expiry_date}
          </p>
        </div>
        <div className="card-action">
          <div className="row">
            {isSuperAdmin && (
              <div className="col">
                <button
                  type="button"
                  className="btn btn-md btn-success"
                  onClick={() => toggleChargeCardModal({ id })}
                >
                  Charge
                </button>
              </div>
            )}
            <div className="col mt-3">
              <img
                alt={card_type}
                src={visaIcon}
                width="60"
                className="float-right"
                style={cardImg ? {} : { opacity: 0 }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreditCardList = ({
  billingAddresses,
  businessId,
  fetchBusinessDetail,
  toggleDeleteModal,
  toggleChargeCardModal,
  isSuperAdmin,
}) =>
  billingAddresses.map(billingAddress => (
    <CreditCardItem
      billingAddress={billingAddress}
      businessId={businessId}
      fetchBusinessDetail={fetchBusinessDetail}
      toggleDeleteModal={toggleDeleteModal}
      toggleChargeCardModal={toggleChargeCardModal}
      isSuperAdmin={isSuperAdmin}
    />
  ));

CreditCardList.defaultProps = {
  billingAddresses: [],
};

const BusinessPlanInfo = ({
  businessName,
  planSubscription: defaultPlanSubscription,
  plans,
  fetchBusinessDetail,
  match,
  billingAddresses,
}) => {
  const businessPlan =
    plans.find(plan => plan.id === defaultPlanSubscription.plan_id) || {};
  const selectedPlanLabel = `${businessPlan.title} Account`;

  const [addCardModalVisible, setAddCardModalVisible] = useState(false);
  const [modalVisible, setModalVisibility] = useState(false);
  const [chargeCardModalVisible, setChargeCardModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisibility] = useState(false);
  const [deleteModalVisible, setDeleteModalVisibility] = useState(false);
  const [deletingCard, setDeletingCard] = useState({});
  const [chargeCard, setChargeCard] = useState({});
  const [planSubscription, setPlanSubscription] = useState({
    label: selectedPlanLabel,
    value: businessPlan,
  });
  const [trialLength, setTrialLength] = useState(DEFAULT_TRIAL_LENGTH);
  const [successModalContent, setSuccessModalContent] = useState('');

  const { end_date } = defaultPlanSubscription;
  const expiryDate = end_date ? formatExpiryDate(end_date) : 'N/A';
  const isSuperAdmin = getRole() === USERS.ADMIN;
  const { params } = match;
  const { id: businessId } = params;

  const onConfirm = () => {
    const { params } = match;
    const { id } = params;
    const planSubscriptionAttributes = getPlanSubscription(
      planSubscription,
      trialLength
    );
    const data = {
      business: { plan_subscription_attributes: planSubscriptionAttributes },
    };
    BusinessApi.update(id, data)
      .then(() => {
        Toaster.getSuccessToaster('check-circle', 'Successfully updated plan');
        setModalVisibility(false);
        fetchBusinessDetail();
      })
      .catch(() =>
        Toaster.getErrorToaster('exclamation-triangle', 'Failed to update plan')
      );
  };

  return (
    <BusinessCardWrapper iconLeft="credit-card" title="Account Information">
      <PlanSelectionModal
        isVisible={modalVisible}
        onCancel={() => setModalVisibility(false)}
        planSubscription={planSubscription}
        plans={plans}
        setPlanSubscription={setPlanSubscription}
        setTrialLength={setTrialLength}
        trialLength={trialLength}
        onConfirm={onConfirm}
      />
      <AddCardSuccessModal
        businessName={businessName}
        isVisible={successModalVisible}
        fetchBusinessDetail={fetchBusinessDetail}
        modalContent={successModalContent}
        toggleSuccessModal={(data = {}) => {
          setSuccessModalVisibility(!successModalVisible);
          setSuccessModalContent(data);
        }}
      />
      <DeleteCardModal
        isVisible={deleteModalVisible}
        deletingCard={deletingCard}
        businessId={businessId}
        fetchBusinessDetail={fetchBusinessDetail}
        toggleDeleteModal={() => {
          setDeleteModalVisibility(!deleteModalVisible);
          setDeletingCard({});
        }}
      />
      <AddCreditCardModal
        isVisible={addCardModalVisible}
        billingAddresses={billingAddresses}
        toggleSuccessModal={(data = {}) => {
          setSuccessModalVisibility(!successModalVisible);
          setSuccessModalContent(data);
        }}
        businessId={businessId}
        onCancel={() => setAddCardModalVisible(false)}
      />
      <ChargeCreditCardModal
        businessName={businessName}
        isVisible={chargeCardModalVisible}
        businessId={businessId}
        chargeCard={chargeCard}
        toggleSuccessModal={(data = {}) => {
          setSuccessModalVisibility(!successModalVisible);
          setChargeCard({});
          setSuccessModalContent(data);
        }}
        onCancel={() => setChargeCardModalVisible(false)}
      />

      <div className="p-3">
        <div className="row">
          <div className="col-md-12">
            <h4 className="mb-3 mt-2 current-plan-heading text-capitalize">
              {businessPlan.title} Plan
            </h4>
            <hr />
          </div>
          <div className="col-md-8">
            <div className="row mb-2">
              <div className="col-md-6">
                <span className="thin-font-weight">Price</span>
              </div>
              <div className="col-md-6">${businessPlan.rate}</div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <span className="thin-font-weight">Expiry Date</span>
              </div>
              <div className="col-md-6">{expiryDate}</div>
            </div>
          </div>
          <div className="col-md-4">
            {!isSuperAdmin && (
              <ManageSubscription
                plan_subscription={defaultPlanSubscription}
                plan_status={businessPlan}
                fetchUserDetails={fetchBusinessDetail}
              >
                <button
                  type="button"
                  className="btn btn-lg btn-success btn-rounded float-right mt-2"
                >
                  Manage
                </button>
              </ManageSubscription>
            )}
            {isSuperAdmin && (
              <button
                type="button"
                className="btn btn-lg btn-success btn-rounded float-right mt-2"
                onClick={() => setModalVisibility(true)}
              >
                Manage
              </button>
            )}
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-md-12 mb-3">
            Payment Method{' '}
            <CustomIconToolTip
              target="payment-tooltip"
              toolTipText="Need to update your Credit Card details? Click + Add Another Card and set it as your primary card, then delete the old card."
              icon="question-circle"
              placement="top"
            />
          </div>
          <div className="col-md-12">
            <div className="row">
              <CreditCardList
                billingAddresses={billingAddresses}
                businessId={businessId}
                fetchBusinessDetail={fetchBusinessDetail}
                isSuperAdmin={isSuperAdmin}
                toggleChargeCardModal={value => {
                  setChargeCardModalVisible(!chargeCardModalVisible);
                  setChargeCard(value);
                }}
                toggleDeleteModal={value => {
                  setDeleteModalVisibility(!deleteModalVisible);
                  setDeletingCard(value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="pt-3 pb-1">
          <button
            type="button"
            onClick={() => setAddCardModalVisible(true)}
            className="btn btn-link text-secondary card-link-btn"
          >
            <FontAwesomeIcon icon="plus" className="text-primary" />
            Add another Card
          </button>
        </div>
      </div>
    </BusinessCardWrapper>
  );
};

export default withRouter(BusinessPlanInfo);



// WEBPACK FOOTER //
// ./src/components/businessDetail/businessPlan/planInfo.js